// Setup Tour — pure logic + selectors.
//
// This module is deliberately FREE of React, the DOM, Supabase, and
// Date.now() side effects (callers inject `now`/`today`). Everything the
// three tour phases need to decide "what shows next" lives here as pure
// functions so the behaviour is unit-testable without a browser:
//   · Part 1 Next stays LOCKED until the current step's server `done`
//     flag flips true (or an optional step is skipped).
//   · Part 2 Next is always FREE (bounded 0..9) — "Got it" moments.
//   · Resume lands on the right phase/step from server-stored progress.
//   · The completion celebration fires exactly once.
//   · The post-tour echo hint obeys "≤ once/day, dead after 7 days OR
//     5 AI uses, whichever first."
//
// The endpoint GET /api/onboarding/status is the single source of truth
// for setup-step completion — nothing here re-derives "is this step
// done"; it only reads the `done` flags the endpoint already computed.

'use strict';

export const PHASES = Object.freeze({
  SETUP: 'setup',
  REVEALS: 'reveals',
  FINALE: 'finale',
  DONE: 'done',
});

// The must-have steps — ONLY things truly required to operate the account.
// Importing a book is NOT one of them: a brand-new agent with no book, or one
// starting from leads, must be able to finish onboarding and use the CRM. So
// 'first_book_source' is recommended/optional (skippable), never a gate.
// (Kept in sync with the server list in api/onboarding/status.js.)
export const MUST_HAVE_KEYS = Object.freeze(['verify_email', 'billing']);

export const REVEAL_COUNT = 10;
export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
export const ECHO_AI_USE_CAP = 5;

// ── Default server-persisted progress blob ────────────────────────────
// Stored in user_state under a single key (see tourState.js). Kept small
// and JSON-serialisable. Cross-device resume falls out of this being
// server-side only (no localStorage).
export const DEFAULT_TOUR_STATE = Object.freeze({
  version: 1,
  phase: PHASES.SETUP,        // setup → reveals → finale → done
  setupStepIndex: 0,          // pointer into the ordered setup-step list
  skipped: {},                // { [stepKey]: true } — optional steps skipped
  revealIndex: 0,             // 0..REVEAL_COUNT-1 across Part 2 stops
  minimized: false,           // Part 1 "Finish later" → Resume pill
  celebrated: false,          // finale celebration already shown once
  completedAt: null,          // ISO timestamp when the finale completed
  dismissed: false,           // closed without finishing (still reopenable)
  echo: {
    firstCompletedAt: null,   // ISO — starts the 7-day echo window
    lastShownDate: null,      // 'YYYY-MM-DD' — throttles to once/day
    aiUseCount: 0,            // post-tour AI sends observed
    disabled: false,          // hard-off once a terminal rule trips
  },
});

// Deep-merge an untrusted stored blob onto the defaults so a partial or
// garbage value from an older schema never crashes a consumer.
export function mergeTourDefaults(raw) {
  if (!raw || typeof raw !== 'object') return cloneDefault();
  const base = cloneDefault();
  const echo = (raw.echo && typeof raw.echo === 'object') ? raw.echo : {};
  return {
    ...base,
    ...raw,
    skipped: (raw.skipped && typeof raw.skipped === 'object') ? { ...raw.skipped } : {},
    echo: { ...base.echo, ...echo },
  };
}

function cloneDefault() {
  return { ...DEFAULT_TOUR_STATE, skipped: {}, echo: { ...DEFAULT_TOUR_STATE.echo } };
}

// ── Part 1: setup steps ───────────────────────────────────────────────

// Order the endpoint's steps: must-have first (in canonical order), then
// the recommended ones in the order the endpoint returned them.
export function orderSetupSteps(statusSteps) {
  const steps = Array.isArray(statusSteps) ? statusSteps.filter(s => s && s.key) : [];
  const byKey = new Map(steps.map(s => [s.key, s]));
  const must = MUST_HAVE_KEYS.map(k => byKey.get(k)).filter(Boolean);
  const mustSet = new Set(MUST_HAVE_KEYS);
  const rest = steps.filter(s => !mustSet.has(s.key));
  return [...must, ...rest];
}

export function isMustHave(step) {
  return !!step && MUST_HAVE_KEYS.includes(step.key);
}

export function isOptionalStep(step) {
  return !!step && !MUST_HAVE_KEYS.includes(step.key);
}

export function isStepDone(step) {
  return !!(step && step.done);
}

// A step is "cleared" (Next may proceed past it) when the server marks it
// done, OR — for optional steps only — when the operator skipped it.
export function isStepCleared(step, skipped) {
  if (isStepDone(step)) return true;
  if (isOptionalStep(step) && skipped && skipped[step.key]) return true;
  return false;
}

// THE lock: Part 1's Next button is enabled only when the current step is
// cleared. Must-have steps can never be skipped, so they're gated purely
// on the server `done` flag.
export function canAdvanceSetup(step, skipped) {
  return isStepCleared(step, skipped);
}

export function allMustHaveDone(statusSteps) {
  const steps = Array.isArray(statusSteps) ? statusSteps : [];
  const byKey = new Map(steps.map(s => [s && s.key, s]));
  return MUST_HAVE_KEYS.every(k => isStepDone(byKey.get(k)));
}

// First step the operator still needs to act on (not done and not
// skipped). Returns orderedSteps.length when every step is cleared.
export function firstUnclearedIndex(orderedSteps, skipped) {
  for (let i = 0; i < orderedSteps.length; i++) {
    if (!isStepCleared(orderedSteps[i], skipped)) return i;
  }
  return orderedSteps.length;
}

// Where to land Part 1 on resume: the stored pointer, clamped. Part 1's
// Next is MANUAL (a completed step keeps its unlocked Next until the
// operator clicks it) — so we resume exactly where they left off rather
// than auto-skipping cleared steps on the next status refetch.
export function resumeSetupIndex(tourState, orderedSteps) {
  const len = orderedSteps.length;
  const stored = Number.isInteger(tourState && tourState.setupStepIndex) ? tourState.setupStepIndex : 0;
  return Math.max(0, Math.min(stored, Math.max(len - 1, 0)));
}

export function setupComplete(orderedSteps, skipped) {
  // Setup "done enough to enter Part 2" is defined as all MUST-HAVE steps
  // done (recommended steps never block per the spec).
  return allMustHaveDone(orderedSteps) || orderedSteps.length === 0;
}

export function setupProgress(orderedSteps, skipped) {
  const total = orderedSteps.length;
  const done = orderedSteps.filter(isStepDone).length;
  return { done, total };
}

// ── Part 2: feature reveals (free navigation) ─────────────────────────

export function clampReveal(i, total = REVEAL_COUNT) {
  const n = Number.isInteger(i) ? i : 0;
  return Math.max(0, Math.min(total - 1, n));
}

export function nextReveal(i, total = REVEAL_COUNT) {
  return clampReveal((Number.isInteger(i) ? i : 0) + 1, total);
}

export function prevReveal(i, total = REVEAL_COUNT) {
  return clampReveal((Number.isInteger(i) ? i : 0) - 1, total);
}

// True when Next on the last reveal should hand off to the AI finale.
export function isLastReveal(i, total = REVEAL_COUNT) {
  return clampReveal(i, total) === total - 1;
}

// ── Phase resolution / view derivation ────────────────────────────────

// Decide which surface (if any) the tour should render. `status` is the
// parsed /api/onboarding/status body, or null when it failed/loading.
// A failed status must HIDE the setup surface quietly — never crash and
// never block the dashboard.
export function deriveView({ tourState, status, statusFailed, reopened }) {
  const ts = mergeTourDefaults(tourState);

  if (ts.phase === PHASES.DONE) {
    // Completed: only the (rule-gated) echo hint remains — unless the
    // operator explicitly reopened the checklist from the sidebar.
    return reopened ? 'setup' : 'echo';
  }

  if (ts.dismissed && !reopened) return 'hidden';

  if (ts.phase === PHASES.REVEALS) return 'reveals';
  if (ts.phase === PHASES.FINALE) return 'finale';

  // phase === SETUP (the default for a brand-new operator).
  // Setup is driven by the status endpoint; if it isn't available yet or
  // failed, show nothing rather than a broken/empty checklist.
  if (statusFailed || !status) return 'hidden';
  return 'setup';
}

// ── Completion + celebration ──────────────────────────────────────────

export function markCompleted(tourState, nowIso) {
  const ts = mergeTourDefaults(tourState);
  return {
    ...ts,
    phase: PHASES.DONE,
    completedAt: nowIso,
    minimized: false,
    dismissed: false,
    celebrated: false,           // celebration hasn't shown yet
    echo: { ...ts.echo, firstCompletedAt: ts.echo.firstCompletedAt || nowIso },
  };
}

// Fires exactly once: true only while phase is DONE and the celebration
// hasn't been acknowledged. Consumers call markCelebrated() after showing.
export function shouldShowCelebration(tourState) {
  const ts = mergeTourDefaults(tourState);
  return ts.phase === PHASES.DONE && ts.completedAt != null && ts.celebrated === false;
}

export function markCelebrated(tourState) {
  const ts = mergeTourDefaults(tourState);
  return { ...ts, celebrated: true };
}

// ── Post-tour echo hint ───────────────────────────────────────────────

export function echoIsExpired(echo, nowMs) {
  if (!echo || !echo.firstCompletedAt) return false;
  const start = Date.parse(echo.firstCompletedAt);
  if (Number.isNaN(start)) return false;
  return (nowMs - start) >= SEVEN_DAYS_MS;
}

// Whether the idle "Stuck? Ask me." hint may show right now.
export function shouldShowEcho(echo, nowMs, todayStr) {
  if (!echo) return false;
  if (echo.disabled) return false;
  if ((echo.aiUseCount || 0) >= ECHO_AI_USE_CAP) return false;
  if (echoIsExpired(echo, nowMs)) return false;
  if (echo.lastShownDate && echo.lastShownDate === todayStr) return false; // once/day
  return true;
}

export function echoAfterShown(echo, todayStr) {
  return { ...echo, lastShownDate: todayStr };
}

// Record one post-tour AI use; auto-disable once the 5-use cap is reached.
export function echoAfterAiUse(echo) {
  const nextCount = (echo && echo.aiUseCount ? echo.aiUseCount : 0) + 1;
  return {
    ...echo,
    aiUseCount: nextCount,
    disabled: nextCount >= ECHO_AI_USE_CAP ? true : !!(echo && echo.disabled),
  };
}
